import React from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import {
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material'

export interface PaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void
}

const PaginationActions: React.FC<PaginationActionsProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
}) => {
  const totalPages = Math.max(0, Math.ceil(count / rowsPerPage) - 1)

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0)
  }

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, page - 1))
  }

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.min(totalPages, page + 1))
  }

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, totalPages)
  }

  const from = count === 0 ? 0 : page * rowsPerPage + 1
  const to = Math.min(count, (page + 1) * rowsPerPage)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        size="small"
        sx={theme => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        })}
      >
        <FirstPage fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        size="small"
        sx={theme => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        })}
      >
        <KeyboardArrowLeft fontSize="small" />
      </IconButton>

      <Typography
        variant="body2"
        sx={theme => ({
          color: theme.palette.text.secondary,
          fontWeight: 500,
          minWidth: 90,
          textAlign: 'center',
        })}
      >
        {`${from}-${to} of ${count}`}
      </Typography>

      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= totalPages}
        aria-label="next page"
        size="small"
        sx={theme => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        })}
      >
        <KeyboardArrowRight fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= totalPages}
        aria-label="last page"
        size="small"
        sx={theme => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.secondary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        })}
      >
        <LastPage fontSize="small" />
      </IconButton>
    </Box>
  )
}

export default PaginationActions
